import { useList, useDelete, useGo } from "@refinedev/core";
import { useState } from "react";
import { Plus, Edit, Trash2, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from "@/components/tables";
import { FormHeader } from "@/components/forms";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";
import { clearPermissionsCache } from "@/providers/access-control-provider";
import type { Role } from "./types";

/**
 * Roles list page with sorting and bulk actions
 */
export function RolesList() {
  useScrollRestoration();
  const go = useGo();
  const [page, setPage] = usePersistedState("roles:page", 1);
  const [search, setSearch] = usePersistedState("roles:search", "");
  const [sort, setSort] = usePersistedState<SortState | undefined>("roles:sort", { field: "displayName", order: "asc" });
  const [deleteRoles, setDeleteRoles] = useState<Role[]>([]);
  const pageSize = 10;

  const { result, query } = useList<Role>({
    resource: "roles",
    pagination: { currentPage: page, pageSize, mode: "server" },
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
    filters: search ? [{ field: "search", operator: "contains" as const, value: search }] : [],
  });

  const data = result.data ?? [];
  const total = result.total ?? 0;
  const isLoading = query.isLoading;

  const deleteMutation = useDelete();
  const dateFormatter = new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const formatDate = (value?: string | null) => {
    if (!value) {return "—";}
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : dateFormatter.format(date);
  };

  const handleDelete = async () => {
    if (deleteRoles.length === 0) {return;}

    // Удаляем все выбранные роли последовательно
    for (const role of deleteRoles) {
      await deleteMutation.mutateAsync({
        resource: "roles",
        id: role.id,
      });
    }

    // Очищаем кэш прав чтобы изменения применились сразу
    clearPermissionsCache();
    setDeleteRoles([]);
  };

  const columns: Column<Role>[] = [
    {
      key: "id",
      header: "ID",
      width: "80px",
      sortable: true,
    },
    {
      key: "displayName",
      header: "Название",
      sortable: true,
      render: (_, role) => (
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{role.displayName}</span>
        </div>
      ),
    },
    {
      key: "description",
      header: "Описание",
      sortable: true,
      render: (value) => (
        <span className="text-muted-foreground text-sm">
          {value ? String(value) : "—"}
        </span>
      ),
    },
    {
      key: "_count.adminUsers",
      header: "Пользователей",
      width: "120px",
      render: (_, role) => role._count?.adminUsers ?? 0,
    },
    {
      key: "permissions",
      header: "Прав",
      width: "100px",
      render: (_, role) => role._count?.permissions ?? 0,
    },
    {
      key: "createdAt",
      header: "Создана",
      sortable: true,
      render: (value) => (typeof value === "string" ? formatDate(value) : "—"),
    },
    {
      key: "updatedAt",
      header: "Обновлена",
      sortable: true,
      render: (value) => (typeof value === "string" ? formatDate(value) : "—"),
    },
  ];

  const bulkActions: BulkAction<Role>[] = [
    {
      label: "Редактировать",
      icon: <Edit className="h-4 w-4" />,
      onClick: (selectedRoles) => {
        // Редактируем первую выбранную роль
        if (selectedRoles.length === 1) {
          go({ to: `/roles/edit/${selectedRoles[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Удалить",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (selectedRoles) => setDeleteRoles(selectedRoles),
      variant: "destructive",
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Роли"
        description="Управление ролями и правами доступа"
        actions={
          <Button onClick={() => go({ to: "/roles/create" })}>
            <Plus className="h-4 w-4 mr-2" />
            Создать роль
          </Button>
        }
        sticky
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Поиск по названию роли..."
      />

      {/* Table with sorting and bulk actions */}
      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(role) => go({ to: `/roles/edit/${role.id}` })}
        emptyMessage="Нет созданных ролей"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteRoles.length > 0} onOpenChange={() => setDeleteRoles([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteRoles.length === 1 ? "Удалить роль?" : `Удалить ${deleteRoles.length} ролей?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRoles.length === 1 ? (
                <>
                  Вы уверены, что хотите удалить роль «{deleteRoles[0]?.displayName}»?
                  Это действие нельзя отменить. Все пользователи с этой ролью
                  потеряют связанные права.
                </>
              ) : (
                <>
                  Вы уверены, что хотите удалить {deleteRoles.length} ролей?
                  Это действие нельзя отменить. Все пользователи с этими ролями
                  потеряют связанные права.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
